import { BaseScreen, i18n } from '@goodboydigital/astro';
import { Point, Sprite, Text, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsEvent } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { Browser } from '../utils/Browser';
import { Panel } from './../ui/Panel';

export class SettingsScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public title!: LocalisedText;
    public version!: Text;
    public languageText!: LocalisedText;
    public nameText!: LocalisedText;
    public rateText!: LocalisedText;
    public termsText!: LocalisedText;
    public privacyText!: LocalisedText;
    public resetText!: LocalisedText;
    public backButton!: NineSliceButton;
    public settingsPanel!: Panel;
    
    private btnWidth = 300;
    private btnHeight = 85;
    private btnFontSize = 32;
    private btnFontStrokeThickness = 6;
    
    public _backToSplash: boolean = false;
    
    constructor(app: App)
    {
        super(app);
        this.app = app;
    }

    async init(): Promise<void>
    {
        
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0.1;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.settingsPanel = new Panel({
            w: 720,
            h: 540,
        });
        this.view.addChild(this.settingsPanel.view);
        this.settingsPanel.view.sortableChildren = true;

        this.title = new LocalisedText('settingsScreen_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 56,
            maxWidth: 650,
        });
        this.title.anchor.set(0.5);
        this.settingsPanel.view.addChild(this.title);
        this.title.y -= 215;

        this.version = new Text(`v${_VERSION_}`, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'right',
            fontSize: 18,
        });
        this.version.anchor.set(1, 0.5);
        this.settingsPanel.view.addChild(this.version);
        this.version.x = (this.settingsPanel.bg.width / 2) - 60;
        this.version.y = (this.settingsPanel.bg.height / 2) - 30;
        this.version.alpha = 0.5;

        const muteIcon = Sprite.from(Shared.assets.getTexture('icon-sound-on'));
        const muteBtn = new NineSliceButton({
            icon: muteIcon,
            accessibleTitle: '',
            w: muteIcon.width * 1.5,
            h: muteIcon.height * 1.5,
        });

        this.settingsPanel.view.addChild(muteBtn.view);
        muteBtn.view.y = -110;
        muteBtn.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { game_item: Shared.settings.muted ? "settings|sound on" : "settings|sound off" });
            Shared.settings.toggleMuted();
        });
        Shared.settings.muteChanged.connect((state) =>
        {
            muteIcon.texture = Shared.assets.getTexture(state ? 'icon-sound-off' : 'icon-sound-on');
        });
        muteIcon.texture = Shared.assets.getTexture(Shared.settings.muted ? 'icon-sound-off' : 'icon-sound-on');
        
        const row1Y = 5; // y offset for row 1 pos
        const row2Y = 95; // y offset for row 2 pos
        const row3Y = 185; // y offset for row 3 pos
        const xDiff = 160; // sets diff from x for button pos
        if(Settings.browserLite){
            muteBtn.view.y = -60;
            this.createResetButton(0, row2Y);  
        }
        else if(Settings.isApp()){
            
            this.createLanguageButton(-xDiff, row1Y);
            this.createChangeIDButton(xDiff, row1Y);
            this.createRateGameButton(-xDiff, row2Y);
            this.createTermsButton(xDiff, row2Y);
            this.createPrivacyPolicyButton(-xDiff, row3Y);
            this.createResetButton(xDiff, row3Y);  
        }
        else{            
            muteBtn.view.y = -60;
            this.createChangeIDButton(0, row2Y);
            this.createResetButton(0, row3Y);  
        }
        
        const backIcon = Sprite.from(Shared.assets.getTexture('icon-back-arrow'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() =>
        {
            if(this._backToSplash){
                this.app.controller.toSplash();
            }
            else{
                this.app.controller.toHome();
            }            
        });
    }
    
    createLanguageButton(x: number, y: number): void
    {
        const languageIcon = new Sprite();

        this.languageText = new LocalisedText('settingsScreen_language', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: this.btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: this.btnFontSize,
            maxWidth: this.btnWidth * 0.9,
        });

        this.languageText.anchor.set(0.5);
        languageIcon.addChild(this.languageText);

        const languageButton = new NineSliceButton({
            icon: languageIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: this.btnWidth,
            h: this.btnHeight,
        });

        languageButton.view.position.set(x, y);

        this.settingsPanel.view.addChild(languageButton.view);
        languageButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { game_item: "settings|language" });
            this.app.controller.toLanguageSelect();
        });
    }
    
    createChangeIDButton(x: number, y: number): void
    {
        const nameIcon = new Sprite();

        this.nameText = new LocalisedText('settingsScreen_name', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: this.btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: this.btnFontSize,
            maxWidth: this.btnWidth * 0.9,
        });

        this.nameText.anchor.set(0.5);
        nameIcon.addChild(this.nameText);

        const nameButton = new NineSliceButton({
            icon: nameIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: this.btnWidth,
            h: this.btnHeight,
        });

        nameButton.view.position.set(x, y);

        this.settingsPanel.view.addChild(nameButton.view);
        nameButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { game_item: "settings|changeid" });
            this.app.controller.toChangeName();
        });
    }
    
    createRateGameButton(x: number, y: number): void
    {
        const rateIcon = new Sprite();

        this.rateText = new LocalisedText('settingsScreen_rate', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: this.btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: this.btnFontSize,
            maxWidth: this.btnWidth * 0.9,
        });

        this.rateText.anchor.set(0.5);
        rateIcon.addChild(this.rateText);

        const rateButton = new NineSliceButton({
            icon: rateIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: this.btnWidth,
            h: this.btnHeight,
        });

        rateButton.view.position.set(x, y);

        this.settingsPanel.view.addChild(rateButton.view);
        rateButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { game_item: "settings|rategame" });
            Browser.open('rate.html');
        });
    }
    
    createTermsButton(x: number, y: number): void
    {
        const termsIcon = new Sprite();

        this.termsText = new LocalisedText('settingsScreen_terms', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: this.btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: this.btnFontSize,
            maxWidth: this.btnWidth * 0.9,
        });

        this.termsText.anchor.set(0.5);
        termsIcon.addChild(this.termsText);

        const termsButton = new NineSliceButton({
            icon: termsIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: this.btnWidth,
            h: this.btnHeight,
        });

        termsButton.view.position.set(x, y);

        this.settingsPanel.view.addChild(termsButton.view);
        termsButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { game_item: "settings|terms" });
            Browser.open(i18n.translate(`termsScreen_link_terms`));
        });
    }
    
    createPrivacyPolicyButton(x: number, y: number): void
    {
        const privacyIcon = new Sprite();

        this.privacyText = new LocalisedText('settingsScreen_privacy', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: this.btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: this.btnFontSize,
            maxWidth: this.btnWidth * 0.9,
        });

        this.privacyText.anchor.set(0.5);
        privacyIcon.addChild(this.privacyText);

        const privacyButton = new NineSliceButton({
            icon: privacyIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: this.btnWidth,
            h: this.btnHeight,
        });

        privacyButton.view.position.set(x, y);
        this.settingsPanel.view.addChild(privacyButton.view);
        privacyButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { game_item: "settings|privacy policy" });
            Browser.open(i18n.translate(`termsScreen_link_privacy`));
        });
    }
    
    createResetButton(x: number, y: number): void
    {
        const resetIcon = new Sprite();

        this.resetText = new LocalisedText('settingsScreen_reset', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: this.btnFontStrokeThickness,
            lineJoin: 'round',
            align: 'center',
            fontSize: this.btnFontSize,
            maxWidth: this.btnWidth * 0.9,
        });

        this.resetText.anchor.set(0.5);
        resetIcon.addChild(this.resetText);

        const resetButton = new NineSliceButton({
            icon: resetIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: this.btnWidth,
            h: this.btnHeight,
        });

        resetButton.view.position.set(x, y);
        this.settingsPanel.view.addChild(resetButton.view);
        resetButton.onPress.connect(() =>
        {
            this.app.analytics.trackEvent(AnalyticsEvent.SETTINGS_MENU_SELECTION_CLICK, { interaction: "settings|reset", game_item: "settings|reset" });
            this.app.popup.presentConfirmPopup({
                title: 'resetPopup_title',
                message: 'resetPopup_message',
                confirm: 'resetPopup_confirm',
                cancel: 'resetPopup_cancel',
                onConfirm: () =>
                {
                    this.app.user.resetData();
                    this.app.reload();
                },
            });
        });
    }
    
    public update(delta = 1): void
    {
        this.app.clouds?.update(delta);
    }
    
    async prepare(screenData: Record<string, any>): Promise<void>
    {
        this._backToSplash = screenData.backToSplash != undefined ? screenData.backToSplash : this._backToSplash;
    }
    
    show(): void
    {
        this.resize(this.width, this.height);

        Shared.game.soundSystem.playMusic('menu-music');

        this.view.visible = true;
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter as Point);

        this.settingsPanel.view.position.copyFrom(this.app.stageCenter as Point);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );
        
        this.app.clouds?.resize(w, h);
    }
}
